import { DateFormatter, getLocalTimeZone } from '@internationalized/date';
import { civilToday } from './civil-date';

/**
 * Instants — system time (`asOf`, `recordedAt`): RFC 3339 in UTC, `…Z` on the wire (SDD-017 L-6).
 *
 * An instant is not a domain date: it is the moment the system recorded a belief, and it IS tied
 * to a timezone. So it is parsed from its wire form here, once, and only ever formatted — never
 * turned into a civil date that a domain rule could read (backbone §0.7).
 */

/** `2026-10-03T09:14:07Z` (or with a fraction) — the only accepted spelling. */
const INSTANT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

export function isInstant(value: unknown): value is string {
  return (
    typeof value === 'string' && INSTANT_PATTERN.test(value) && !Number.isNaN(Date.parse(value))
  );
}

/**
 * Presentation only: an instant in the user's zone. Today's instants show the time alone
 * ("11:14"), any other day date and time — the shape of the grid header's "erfasst …".
 * `precise` always shows the date and the time to the second: what a list of recorded moments
 * needs, where two of them can be seconds apart on the same day.
 */
export function formatInstant(
  value: string,
  locale: string,
  timeZone: string = getLocalTimeZone(),
  { precise = false }: { readonly precise?: boolean } = {},
): string {
  if (!isInstant(value)) return value;
  const at = new Date(Date.parse(value));
  if (precise) {
    return new DateFormatter(locale, {
      timeZone,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(at);
  }
  const day = new DateFormatter('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
  const time: Intl.DateTimeFormatOptions = { timeZone, hour: '2-digit', minute: '2-digit' };
  if (day === civilToday(timeZone)) return new DateFormatter(locale, time).format(at);
  return new DateFormatter(locale, {
    ...time,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(at);
}
